import React, { useState, useEffect, useContext, createContext, ReactNode } from "react";
import { AppContext } from "../app";

const AbTestContext = createContext<{
  getAssignment: (params: { userId: string; testKey: string }) => string | false | undefined;
  updateAssignment: (params: { userId: string; testKey: string; segment: string | false }) => void;
}>({ getAssignment: () => false, updateAssignment: () => null });

export default function useAbTestSegment<T extends string>({
  testKey,
  segments,
}: {
  testKey: string;
  segments: T[];
}): T | undefined {
  const { csrfToken, userId } = useContext(AppContext);
  const { getAssignment, updateAssignment } = useContext(AbTestContext);

  const cachedAssignment = getAssignment({ userId, testKey });
  useEffect(() => {
    async function effect() {
      if (typeof cachedAssignment === "undefined") {
        updateAssignment({ userId, testKey, segment: false });
        const response = await fetch("/ab_tests/check_segment", {
          method: "POST",
          body: JSON.stringify({ test_key: testKey, segments }),
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": `${csrfToken}`,
          },
        });
        const json = await response.json();
        updateAssignment({ userId, testKey, segment: json.segment });
      }
    }

    effect();
  });
  return cachedAssignment ? (cachedAssignment as T) : undefined;
}

export function AbTestProvider({ children }: { children: ReactNode }) {
  const [testAssignments, setTestAssignments] = useState<{ [testKey: string]: string | false }>({});

  return (
    <AbTestContext.Provider
      value={{
        getAssignment: ({ userId, testKey }) => testAssignments[`${userId}:${testKey}`],
        updateAssignment: ({ userId, testKey, segment }) => {
          setTestAssignments({ ...testAssignments, [`${userId}:${testKey}`]: segment });
        },
      }}
    >
      {children}
    </AbTestContext.Provider>
  );
}
